// The WGSL twins of the two full-quad vertex sources and of the two screen
// passes (`../../gfx/worldquad.ts`).
//
// The world quad and the screen quad are the vertex halves every map pass
// and every veil shares, so each one is the `@vertex fn vs` entry ALONE: a
// pass joins it to the generated header and to its own fragment half.
//
// The clip position is the GLSL's, unchanged. NDC is the same in both APIs
// on the way to the canvas, so an on-screen pass needs no y flip; the one
// place the row order differs is the offscreen field pass, whose own twin
// negates clip y.

import { cornerDecodeWgsl } from '../../gl/corner';

/**
 * The world-space vertex twin.
 *
 * The quad spans `[-uPad, WORLD + COL_PITCH/2 + uPad]` by
 * `[-uPad, WORLD_H + uPad]`: odd rows are shifted right by half a column
 * pitch, so their hexes reach past the playable width, and the pad lets a
 * border stroke stand outside the world edge the way the traced reference
 * does.
 */
export const WORLD_VERTEX_WGSL = `@vertex
fn vs(in: VsIn) -> VsOut {
  var out: VsOut;
${cornerDecodeWgsl()}
  let lo = vec2f(-U.uPad, -U.uPad);
  let hi = vec2f(U.uWorld.x + U.uColPitch * 0.5 + U.uPad, U.uWorld.y + U.uPad);
  out.vWorld = mix(lo, hi, corner);
  out.vUV = corner;
  out.pos = vec4f((U.uViewProj * vec3f(out.vWorld, 1.0)).xy, 0.0, 1.0);
  return out;
}
`;

/**
 * The screen-space vertex twin.
 *
 * `vUV` is (0,0) at the top left, which is the orientation the canvas
 * gradients of `render/water.ts` are drawn in. `vWorld` still holds the
 * world point under the pixel, because `uViewInv` maps device px to world
 * px and the corners are exactly the corners of the drawing surface.
 */
export const SCREEN_VERTEX_WGSL = `@vertex
fn vs(in: VsIn) -> VsOut {
  var out: VsOut;
${cornerDecodeWgsl()}
  let dev = corner * U.uRes;
  out.vWorld = (U.uViewInv * vec3f(dev, 1.0)).xy;
  out.vUV = vec2f(corner.x, 1.0 - corner.y);
  out.pos = vec4f(corner * 2.0 - 1.0, 0.0, 1.0);
  return out;
}
`;

/**
 * The deep-water gradient behind everything: three stops down the viewport,
 * opaque, and so the frame never needs a clear colour of its own.
 */
export const BACKGROUND_FRAGMENT_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let t = in.vUV.y;
  let c = select(
    mix(U.uMidColor, U.uLowColor, (t - 0.5) * 2.0),
    mix(U.uTopColor, U.uMidColor, t * 2.0),
    t < 0.5);
  return vec4f(c, 1.0);
}
`;

/**
 * The corner darkening. The reference stretches a 512² radial gradient over
 * the window, so the falloff is measured in half-viewport units on each
 * axis, and the four figures reach the shader as uniforms.
 */
export const VIGNETTE_FRAGMENT_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let d = length((in.vUV - 0.5) * 2.0);
  let t = clamp((d - U.uInner) / max(U.uOuter - U.uInner, 1e-5), 0.0, 1.0);
  let a = t * U.uVignetteAlpha;
  return vec4f(U.uVignetteColor * a, a);
}
`;
